import { Button, Modal, Tabs } from 'design-system';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import Alert from '../Alert/Alert';
import LoginPage from '../../containers/NewAuth/LoginForm';
import RegisterPage from '../../containers/NewAuth/RegisterForm';
import { addQueryParam, makeReturnUrl } from '../../lib/returnUrl';
import { getGuestCartToken } from '../../lib/guestCart';

// Inline join sheet: embeds Login and Registration without hard navigation
export const JoinSheet = ({
  open,
  onOpenChange,
  subjectName = null,
  reloadOnAuth = false,
  noticeKind = null,
  reason = null,
}) => {
  const intl = useIntl();
  const location = useLocation();
  const fallbackName = intl.formatMessage({
    id: 'join_sheet_name_fallback',
    defaultMessage: 'the people you care about',
  });
  const resolvedName = subjectName ? subjectName?.trim() || fallbackName : null;
  const isGenericAuthGate = !resolvedName && !reason;
  const intentCopy = useMemo(() => {
    switch (reason) {
      case 'react':
        return {
          titleId: 'join_sheet_intent_react_title',
          titleDefault: 'Sign in to react to sparks',
          introId: 'join_sheet_intent_react_intro',
          introDefault: 'When you are signed in, you can react and take part.',
        };
      case 'weekly_question':
        return {
          titleId: 'join_sheet_intent_weekly_question_title',
          titleDefault: "Sign in to answer this week's question",
          introId: 'join_sheet_intent_weekly_question_intro',
          introDefault: 'When you are signed in, you can publish your own spark.',
        };
      case 'reflect':
        return {
          titleId: 'join_sheet_intent_reflect_title',
          titleDefault: 'Sign in to add a reflection',
          introId: 'join_sheet_intent_reflect_intro',
          introDefault: 'When you are signed in, you can share your reflection.',
        };
      case 'composer':
        return {
          titleId: 'join_sheet_intent_composer_title',
          titleDefault: 'Sign in to write a comment',
          introId: 'join_sheet_intent_composer_intro',
          introDefault: 'When you are signed in, you can join the conversation.',
        };
      case 'submit':
        return {
          titleId: 'join_sheet_intent_submit_title',
          titleDefault: 'Sign in to publish your comment',
          introId: 'join_sheet_intent_submit_intro',
          introDefault: 'When you are signed in, your comment can be shared.',
        };
      default:
        return null;
    }
  }, [reason]);

  const handleAuthSuccess = useCallback(() => {
    onOpenChange?.(false);
    if (reloadOnAuth && typeof window !== 'undefined') {
      window.location.reload();
    }
  }, [onOpenChange, reloadOnAuth]);
  const [activeTab, setActiveTab] = useState('login');

  const tabItems = useMemo(
    () => [
      {
        id: 'login',
        label: <FormattedMessage id="auth_login" defaultMessage="Login" />,
        content: <LoginPage isJoinSheet={true} onAuthSuccess={handleAuthSuccess} />,
      },
      {
        id: 'register',
        label: <FormattedMessage id="auth_register" defaultMessage="Register" />,
        content: <RegisterPage isJoinSheet={true} />,
      },
    ],
    [handleAuthSuccess],
  );

  useEffect(() => {
    if (!open) return;
    setActiveTab('login');
    try {
      const existing = localStorage.getItem('ng.returnUrl') || '';
      const token = getGuestCartToken();
      let currentPath = makeReturnUrl(location);
      if (token) {
        currentPath = addQueryParam(currentPath, 'guestCartToken', token);
      } else if (existing.includes('guestCartToken=')) {
        currentPath = existing;
      }
      if (currentPath) {
        localStorage.setItem('ng.returnUrl', currentPath);
      }
    } catch {}
  }, [open, location]);

  return (
    <Modal
      open={open}
      onClose={() => onOpenChange?.(false)}
      title={
        <FormattedMessage
          id={
            intentCopy?.titleId ||
            (isGenericAuthGate ? 'join_sheet_auth_title' : 'join_sheet_title')
          }
          defaultMessage={
            intentCopy?.titleDefault ||
            (isGenericAuthGate ? 'Sign in to continue' : 'Choose your relation to {name}')
          }
          values={{ name: resolvedName || fallbackName }}
        />
      }
      footer={null}
      fullscreen="mobile"
      style={{ width: 600 }}
      centered
    >
      {noticeKind === 'guest_quota_exceeded' && (
        <Alert>
          <div className="alert-title">
            <FormattedMessage
              id="guest_quota_notice_title"
              defaultMessage="More searches today"
            />
          </div>
          <div className="alert-message">
            <FormattedMessage
              id="guest_quota_notice_body"
              defaultMessage="To keep searching today, log in or create an account."
            />
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <Button variant="subtle" onClick={() => setActiveTab('login')}>
              <FormattedMessage id="auth_login" defaultMessage="Log in" />
            </Button>
            <Button variant="primary" onClick={() => setActiveTab('register')}>
              <FormattedMessage id="auth_register" defaultMessage="Create account" />
            </Button>
          </div>
        </Alert>
      )}
      {(resolvedName || intentCopy || isGenericAuthGate) && (
        <>
          <div style={{ marginBottom: 8, color: 'var(--text)' }}>
            <FormattedMessage
              id={
                intentCopy?.introId ||
                (isGenericAuthGate ? 'join_sheet_auth_intro' : 'join_sheet_intro')
              }
              defaultMessage={
                intentCopy?.introDefault ||
                (isGenericAuthGate
                  ? 'You need to sign in to react, reflect, or comment.'
                  : 'Join to stay close to {name}.')
              }
              values={{ name: resolvedName }}
            />
          </div>
        </>
      )}
      <Tabs tabs={tabItems} defaultTab="login" activeTab={activeTab} onTabChange={setActiveTab} />
    </Modal>
  );
};

export default JoinSheet;
